import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import CloudMultiSelect from './CloudMultiSelect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingModal = ({ isOpen, onComplete, userEmail, allowClose = false, initialSelection = [] }) => {
  const [selected, setSelected] = useState(
    initialSelection.length > 0
      ? initialSelection
      : (() => {
          try {
            return JSON.parse(localStorage.getItem('selected_providers') || '[]');
          } catch {
            return [];
          }
        })()
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContinue = async () => {
    if (selected.length === 0) {
      toast.error('Please select at least one cloud provider');
      return;
    }
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(
          `${API_URL}/api/user/preferences`,
          { selected_providers: selected, onboarding_completed: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      localStorage.setItem('selected_providers', JSON.stringify(selected));
      localStorage.setItem('onboarding_completed', 'true');
      window.dispatchEvent(new Event('providers-updated'));
      toast.success(`Tailored CARTIEAI to ${selected.length} cloud${selected.length > 1 ? 's' : ''}.`);
      if (onComplete) onComplete(selected);
    } catch {
      localStorage.setItem('selected_providers', JSON.stringify(selected));
      localStorage.setItem('onboarding_completed', 'true');
      window.dispatchEvent(new Event('providers-updated'));
      if (onComplete) onComplete(selected);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (open) => {
    if (allowClose && !open && onComplete) {
      onComplete(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-visible"
        hideCloseButton={!allowClose}
        data-testid="onboarding-modal"
      >
        <div className="bg-emerald-600 bg-gradient-to-r from-emerald-600 to-indigo-600 p-5 text-white rounded-t-lg relative">
          {allowClose && (
            <button
              onClick={() => handleClose(false)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="onboarding-close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1.5">
              <Sparkles className="w-5 h-5" />
              <Badge className="bg-white/20 text-white border-0 text-[11px]">Quick Setup</Badge>
            </div>
            <DialogTitle className="text-xl text-white">Which clouds do you use?</DialogTitle>
            <DialogDescription className="text-cyan-100 text-sm">
              Pick one or more. We'll tailor CARTIEAI to your stack.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-5 space-y-4">
          <CloudMultiSelect
            value={selected}
            onChange={setSelected}
            mode="multi"
            placeholder="Click to select your clouds…"
            dataTestId="onboarding-cloud-picker"
          />
          <p className="text-[11px] text-slate-400 text-center">
            15 clouds supported (6 live today: AWS, Azure, GCP, Snowflake, Databricks, DigitalOcean. 9 more integrating: Oracle, IBM, Alibaba, and others).
          </p>
        </div>

        <div className="px-5 pb-5 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-400" data-testid="onboarding-selected-count">
            {selected.length} selected
          </p>
          <Button
            onClick={handleContinue}
            disabled={selected.length === 0 || isSubmitting}
            className="bg-cyan-600 hover:bg-cyan-700 gap-2 h-9"
            data-testid="onboarding-continue-btn"
          >
            {isSubmitting ? 'Setting up…' : 'Get Started'}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const useOnboarding = () => {
  const isCompleted = localStorage.getItem('onboarding_completed') === 'true';
  const selectedProviders = JSON.parse(localStorage.getItem('selected_providers') || '[]');
  return {
    needsOnboarding: !isCompleted,
    selectedProviders,
    isProviderSelected: (id) => selectedProviders.includes(id),
    hasAnyCloudProvider: selectedProviders.length > 0,
    hasSnowflake: selectedProviders.includes('snowflake'),
  };
};

export const usePermissions = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = user.role || 'user';
  return {
    isAdmin: role === 'admin',
    canManageUsers: role === 'admin',
    canViewAuditLogs: role === 'admin',
    canManageSettings: role === 'admin',
    canViewBilling: role === 'admin',
    role,
  };
};

export default OnboardingModal;
